import React, { useCallback } from 'react'
import classNames from 'classnames'
import {
  ControllerFieldState,
  FieldValues,
  useController,
  UseControllerProps,
  UseControllerReturn,
} from 'react-hook-form'
import { Input } from '..'
import { InputProps } from '../Input'
import { IntlShape, useIntl } from 'react-intl'

const MIN_PASSWORD_LENGTH = 8

const hasBothDigitsAndLetters = (text: string) =>
  /\D/.test(text) && /[0-9]/.test(text)

type GuideProps<T extends FieldValues> = {
  field: UseControllerReturn<T>['field']
  fieldState: ControllerFieldState
  intl: IntlShape
}

function Guide<T extends FieldValues>({
  field: { value },
  fieldState: { invalid, isDirty },
  intl,
}: GuideProps<T>) {
  const getPasswordScore = useCallback(() => {
    if (invalid || !isDirty) {
      return {
        message: intl.formatMessage(
          {
            id: 'boarding.form.password.guide',
            defaultMessage: 'Use at least {length} characters',
          },
          {
            length: MIN_PASSWORD_LENGTH,
          }
        ),
        score: isDirty ? 1 : 0,
      }
    }

    if (!hasBothDigitsAndLetters(value as string)) {
      return {
        message: intl.formatMessage({
          id: 'boarding.form.password.guide.notBest',
          defaultMessage: 'Not the best but it should be ok.',
        }),
        score: 2,
      }
    }

    return {
      message: intl.formatMessage({
        id: 'boarding.form.password.guide.good',
        defaultMessage: 'Looks good, you are ready to go.',
      }),
      score: 3,
    }
  }, [intl, invalid, isDirty, value])

  const { score, message } = getPasswordScore()

  return (
    <div className="margin-vertical-12">
      <div className="flexbox margin-bottom-12">
        {[1, 2, 3].map((index) => (
          <div
            key={index}
            className={classNames('flexbox-grow flexbox-basis-full', {
              'background-shade-50': score === 0 || score < index,
              'background-red': score === 1 && score >= index,
              'background-yellow': score === 2 && score >= index,
              'background-green': score === 3 && score >= index,
              'margin-right-8': index !== 3,
            })}
            style={{ height: 3, borderRadius: 2 }}
          />
        ))}
      </div>
      <div className="flexbox-grow flexbox-basis-full shade-10 font-weight-regular">
        {message}
      </div>
    </div>
  )
}

type PasswordInputProps<T extends FieldValues> = UseControllerProps<T> & {
  hiddenEmail?: string | null
} & Partial<InputProps>

/**
 *
 * @param hiddenEmail [DOM] Password forms should have (optionally hidden) username fields for accessibility: (More info: goo.gl/9p2vKq)
 */
export function PasswordInput<T extends FieldValues>({
  name,
  control,
  hiddenEmail,
  autoFocus = true,
  ...props
}: PasswordInputProps<T>) {
  const intl = useIntl()
  const { field, fieldState } = useController<T>({
    name,
    control,
    rules: {
      minLength: MIN_PASSWORD_LENGTH,
      required: true,
    },
  })
  return (
    <div>
      {hiddenEmail && (
        <Input
          hidden
          type="text"
          name="username"
          autoComplete="email"
          className="border-none"
          style={{ display: 'none' }}
          value={hiddenEmail}
          readOnly
        />
      )}
      <Input
        type="password"
        autoComplete="new-password"
        placeholder={intl.formatMessage({
          id: 'boarding.form.password.placeholder',
          defaultMessage: 'Password',
        })}
        data-test-id="passwordField"
        autoFocus={autoFocus}
        error={fieldState.invalid}
        {...field}
        {...props}
      />
      <Guide field={field} fieldState={fieldState} intl={intl} />
    </div>
  )
}
